import React, { useEffect, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { API, pacienteJsonHeaders } from "../../services/pacienteConfig";
import { EMPTY_HISTORIA_FORM } from "../../services/historiaClinica";
import { PacienteTabNavigation } from "../../components/paciente/PacientePortalNav";
import "./PacienteHistoriaClinicaPage.css";

const YES_NO_FIELDS = new Set([
  "padreVive", "madreVive", "hermanos", "enfermedad", "tratamientoMedico",
  "deporte", "malestarDeporte", "alergico", "diabetico", "problemaCardiaco",
  "presionAlta", "fuma", "otroProfesional",
  "tomoMedicamento", "resultados", "sangradoEncias",
]);

const TAB_CONFIG = [
  { id: 1, label: "Datos", shortLabel: "Datos" },
  { id: 2, label: "Antecedentes", shortLabel: "Antec." },
  { id: 3, label: "Odontologica", shortLabel: "Odonto." },
  { id: 4, label: "Consentimiento", shortLabel: "Firma" },
];

const FIELD_SECTIONS = {
  1: [
    { name: "lugar", label: "Lugar (vive)" },
    { name: "fecha", label: "Fecha de Consulta", type: "date" },
    { name: "odontologo", label: "Odontologo" },
    { name: "paciente", label: "Paciente" },
    { name: "obraSocial", label: "Obra Social" },
    { name: "fechaNac", label: "Fecha de Nacimiento", type: "date" },
    { name: "tel", label: "Telefono" },
    { name: "edad", label: "Edad" },
    { name: "estadoCivil", label: "Estado Civil" },
    { name: "nacionalidad", label: "Nacionalidad" },
    { name: "dni", label: "DNI" },
    { name: "cel", label: "Celular" },
    { name: "domicilio", label: "Domicilio" },
    { name: "profesion", label: "Profesion/Actividad" },
    { name: "trabajo", label: "Lugar de trabajo" },
    { name: "jerarquia", label: "Jerarquia" },
    { name: "titular", label: "Titular" },
  ],
  2: [
    { name: "padreVive", label: "Padre con vida" },
    { name: "enfPadre", label: "Enfermedad padre" },
    { name: "madreVive", label: "Madre con vida" },
    { name: "enfMadre", label: "Enfermedad madre" },
    { name: "hermanos", label: "Hermanos" },
    { name: "hermanosSanos", label: "Sanos (los hermanos)" },
    { name: "enfermedad", label: "Sufre alguna enfermedad" },
    { name: "enfermedadDetalle", label: "Detalle enfermedad" },
    { name: "tratamientoMedico", label: "Tratamiento medico" },
    { name: "tratamientoDetalle", label: "Detalle tratamiento" },
    { name: "medicamentosHabituales", label: "Medicamentos habituales", multiline: true },
    { name: "medicamentosUltimos5", label: "Medicamentos ultimos 5 anos", multiline: true },
    { name: "deporte", label: "Deporte" },
    { name: "malestarDeporte", label: "Malestar al realizarlo" },
    { name: "alergico", label: "Alergico" },
    { name: "alergiasDetalle", label: "Detalle alergias" },
    { name: "problemaCardiaco", label: "Problema cardiaco" },
    { name: "cualCardiaco", label: "Cual" },
    { name: "diabetico", label: "Diabetico" },
    { name: "presionAlta", label: "Presion alta" },
    { name: "fuma", label: "Fuma" },
    { name: "otrasEnfermedades", label: "Observaciones/Otras", multiline: true },
  ],
  3: [
    { name: "motivoConsulta", label: "Motivo de consulta", multiline: true },
    { name: "otroProfesional", label: "Consulto antes con otro profesional" },
    { name: "tomoMedicamento", label: "Tomo medicamentos" },
    { name: "medicamentosConsulta", label: "Nombre de medicamentos", multiline: true },
    { name: "desdeCuando", label: "Desde cuando" },
    { name: "resultados", label: "Obtuvo resultados" },
    { name: "tipoDolor", label: "Tipo de dolor" },
    { name: "localizadoDonde", label: "Localizado en" },
    { name: "irradiadoHacia", label: "Irradiado hacia" },
    { name: "calmaCon", label: "Puede calmarlo con algo" },
    { name: "sangradoEncias", label: "Sangrado encias" },
  ],
  4: [
    { name: "consentimientoNombre", label: "Nombre" },
    { name: "consentimientoDni", label: "DNI" },
    { name: "consentimientoDomicilio", label: "Domicilio" },
    { name: "consentimientoDr", label: "Dr/a MP" },
  ],
};

function mapDbToFormData(dbData = {}) {
  return {
    ...EMPTY_HISTORIA_FORM,
    lugar: dbData.lugar || "",
    fecha: dbData.fecha_consulta || "",
    odontologo: dbData.odontologo || "",
    matricula: dbData.matricula || "",
    paciente: dbData.paciente || "",
    obraSocial: dbData.obra_social || "",
    fechaNac: dbData.fecha_nacimiento || "",
    tel: dbData.telefono || "",
    edad: dbData.edad || "",
    estadoCivil: dbData.estado_civil || "",
    nacionalidad: dbData.nacionalidad || "",
    dni: dbData.dni || "",
    cel: dbData.celular || "",
    domicilio: dbData.domicilio || "",
    profesion: dbData.profesion || "",
    titular: dbData.titular || "",
    trabajo: dbData.lugar_trabajo || "",
    jerarquia: dbData.jerarquia || "",
    padreVive: dbData.padre_vive || "",
    enfPadre: dbData.enf_padre || "",
    madreVive: dbData.madre_vive || "",
    enfMadre: dbData.enf_madre || "",
    hermanos: dbData.hermanos || "",
    hermanosSanos: dbData.hermanos_sanos || "",
    enfermedad: dbData.enfermedad_personal || "",
    enfermedadDetalle: dbData.enfermedad_detalle || "",
    tratamientoMedico: dbData.tratamiento_medico || "",
    tratamientoDetalle: dbData.tratamiento_detalle || "",
    medicamentosHabituales: dbData.medicamentos_habituales || "",
    medicamentosUltimos5: dbData.medicamentos_ultimos_5 || "",
    deporte: dbData.deporte || "",
    malestarDeporte: dbData.malestar_deporte || "",
    alergico: dbData.alergico || "",
    alergiasDetalle: dbData.alergias_detalle || "",
    cicatrizacion: dbData.cicatrizacion || "",
    colageno: dbData.colageno || "",
    fiebreReumatica: dbData.fiebre_reumatica || "",
    medicacionProteccion: dbData.medicacion_proteccion || "",
    diabetico: dbData.diabetico || "",
    controlDiabetes: dbData.control_diabetes || "",
    problemaCardiaco: dbData.problema_cardiaco || "",
    cualCardiaco: dbData.cual_cardiaco || "",
    aspirinaAnticoagulante: dbData.aspirina_anticoagulante || "",
    frecuenciaAnticoagulante: dbData.frecuencia_anticoagulante || "",
    presionAlta: dbData.presion_alta || "",
    chagas: dbData.chagas || "",
    tratamientoChagas: dbData.tratamiento_chagas || "",
    problemaRenal: dbData.problema_renal || "",
    ulcera: dbData.ulcera || "",
    hepatitis: dbData.hepatitis || "",
    tipoHepatitis: dbData.tipo_hepatitis || "",
    problemaHepatico: dbData.problema_hepatico || "",
    cualHepatico: dbData.cual_hepatico || "",
    convulsiones: dbData.convulsiones || "",
    epilepsia: dbData.epilepsia || "",
    medicacionEpilepsia: dbData.medicacion_epilepsia || "",
    sifilis: dbData.sifilis || "",
    infectoContagiosa: dbData.infecto_contagiosa || "",
    transfusiones: dbData.transfusiones || "",
    operado: dbData.operado || "",
    deQueOperacion: dbData.de_que_operacion || "",
    cuandoOperacion: dbData.cuando_operacion || "",
    problemaRespiratorio: dbData.problema_respiratorio || "",
    cualRespiratorio: dbData.cual_respiratorio || "",
    fuma: dbData.fuma || "",
    embarazada: dbData.embarazada || "",
    mesesEmbarazo: dbData.meses_embarazo || "",
    otrasEnfermedades: dbData.otras_enfermedades || "",
    tratamientosAlternativos: dbData.tratamientos_alternativos || "",
    medicoClinico: dbData.medico_clinico || "",
    clinicaDerivacion: dbData.clinica_derivacion || "",
    motivoConsulta: dbData.motivo_consulta || "",
    otroProfesional: dbData.otro_profesional || "",
    tomoMedicamento: dbData.tomo_medicamento || "",
    medicamentosConsulta: dbData.medicamentos_consulta || "",
    desdeCuando: dbData.desde_cuando || "",
    resultados: dbData.resultados || "",
    tipoDolor: dbData.tipo_dolor || "",
    localizadoDonde: dbData.localizado_donde || "",
    irradiadoHacia: dbData.irradiado_hacia || "",
    calmaCon: dbData.calma_con || "",
    golpeDientes: dbData.golpe_dientes || "",
    cuandoGolpe: dbData.cuando_golpe || "",
    comoGolpe: dbData.como_golpe || "",
    fracturaDiente: dbData.fractura_diente || "",
    cualDiente: dbData.cual_diente || "",
    tratamientoFractura: dbData.tratamiento_fractura || "",
    dificultadHablar: dbData.dificultad_hablar || "",
    dificultadMasticar: dbData.dificultad_masticar || "",
    dificultadAbrirBoca: dbData.dificultad_abrir_boca || "",
    dificultadTragar: dbData.dificultad_tragar || "",
    manchas: dbData.manchas || "",
    abultamientos: dbData.abultamientos || "",
    ulceraciones: dbData.ulceraciones || "",
    ampollas: dbData.ampollas || "",
    otrosLesiones: dbData.otros_lesiones || "",
    sangradoEncias: dbData.sangrado_encias || "",
    cuandoSangrado: dbData.cuando_sangrado || "",
    pus: dbData.pus || "",
    deDondePus: dbData.de_donde_pus || "",
    movilidadDientes: dbData.movilidad_dientes || "",
    dientesAltos: dbData.dientes_altos || "",
    caraHinchada: dbData.cara_hinchada || "",
    tratamientoCara: dbData.tratamiento_cara || "",
    azucarDiario: dbData.azucar_diario || "",
    indicePlaca: dbData.indice_placa || "",
    higieneBucal: dbData.higiene_bucal || "",
    sarro: dbData.sarro || "",
    enfermedadPeriodontal: dbData.enfermedad_periodontal || "",
    consentimientoNombre: dbData.consentimiento_nombre || "",
    consentimientoDni: dbData.consentimiento_dni || "",
    consentimientoDomicilio: dbData.consentimiento_domicilio || "",
    consentimientoDr: dbData.consentimiento_dr || "",
    firmaBase64: dbData.firma_base64 || "",
  };
}

export default function PacienteHistoriaClinicaPage() {
  const sigCanvas = useRef(null);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveFeedback, setSaveFeedback] = useState(null);
  const [formData, setFormData] = useState(EMPTY_HISTORIA_FORM);

  useEffect(() => {
    const fetchHistoria = async () => {
      try {
        const response = await fetch(`${API}/paciente/historia-clinica`, {
          headers: pacienteJsonHeaders(),
        });
        const result = await response.json();

        if (result.success && result.data) {
          setFormData(mapDbToFormData(result.data));
        }
      } catch (error) {
        console.error("Error al cargar historia:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchHistoria();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setSaveFeedback(null);
  };

  const handleClearFirma = () => {
    sigCanvas.current?.clear();
    setFormData((prev) => ({ ...prev, firmaBase64: "" }));
    setSaveFeedback(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let firma = formData.firmaBase64;
      if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
        firma = sigCanvas.current.toDataURL("image/png");
      }

      const response = await fetch(`${API}/paciente/historia-clinica`, {
        method: "POST",
        headers: pacienteJsonHeaders(),
        body: JSON.stringify({ ...formData, firma }),
      });
      const data = await response.json();

      if (data.success) {
        setFormData((prev) => ({ ...prev, firmaBase64: firma }));
        setSaveFeedback({ type: "success", text: "Guardado correctamente" });
        setTimeout(() => setSaveFeedback(null), 3000);
      } else {
        setSaveFeedback({ type: "error", text: "Error al guardar" });
      }
    } catch (error) {
      setSaveFeedback({ type: "error", text: "Error de red" });
    } finally {
      setSaving(false);
    }
  };

  const renderField = ({ name, label, type = "text", multiline = false }) => {
    if (YES_NO_FIELDS.has(name)) {
      return (
        <label key={name} className="phc-field">
          <span>{label}</span>
          <select name={name} value={formData[name] || ""} onChange={handleChange}>
            <option value=""></option>
            <option value="SI">SI</option>
            <option value="NO">NO</option>
          </select>
        </label>
      );
    }

    return (
      <label key={name} className="phc-field">
        <span>{label}</span>
        {multiline ? (
          <textarea name={name} value={formData[name] || ""} onChange={handleChange} />
        ) : (
          <input type={type} name={name} value={formData[name] || ""} onChange={handleChange} />
        )}
      </label>
    );
  };

  if (loading) {
    return (
      <div className="paciente-hc-page">
        <div className="paciente-hc-loading">Cargando historia clinica...</div>
        <PacienteTabNavigation activeTab="hc" />
      </div>
    );
  }

  return (
    <div className="paciente-hc-page">
      <header className="phc-header">
        <h2>Mi Historia Clinica</h2>
      </header>

      <div className="phc-tabs">
        {TAB_CONFIG.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`phc-tab-btn ${step === tab.id ? "active" : ""}`}
            onClick={() => setStep(tab.id)}
          >
            {tab.shortLabel}
          </button>
        ))}
      </div>

      <main className="phc-content">
        <section className="phc-section">
          <h3>{TAB_CONFIG.find((tab) => tab.id === step)?.label}</h3>
          <div className="phc-grid">
            {FIELD_SECTIONS[step].map(renderField)}
          </div>

          {step === 4 && (
            <div className="phc-signature">
              <label className="phc-signature-label">Firma del Paciente</label>
              {formData.firmaBase64 && (
                <div className="phc-signature-preview">
                  <span>Firma guardada</span>
                  <img src={formData.firmaBase64} alt="Firma guardada del paciente" />
                </div>
              )}
              <div className="phc-signature-canvas-wrap">
                <SignatureCanvas
                  penColor="#0a2e5c"
                  canvasProps={{ className: "phc-signature-canvas" }}
                  ref={sigCanvas}
                />
              </div>
              <button type="button" className="phc-secondary-btn" onClick={handleClearFirma}>
                Limpiar firma
              </button>
            </div>
          )}
        </section>
      </main>

      <footer className="phc-footer">
        {saveFeedback && <div className={`phc-feedback ${saveFeedback.type}`}>{saveFeedback.text}</div>}
        <div className="phc-nav-actions">
          <button
            type="button"
            className="phc-secondary-btn"
            onClick={() => setStep((current) => Math.max(current - 1, 1))}
            disabled={step === 1}
          >
            Anterior
          </button>
          <button className="phc-save-btn" type="button" onClick={handleSave} disabled={saving}>
            {saving ? "Guardando..." : "Guardar"}
          </button>
          <button
            type="button"
            className="phc-secondary-btn"
            onClick={() => setStep((current) => Math.min(current + 1, TAB_CONFIG.length))}
            disabled={step === TAB_CONFIG.length}
          >
            Siguiente
          </button>
        </div>
      </footer>

      <PacienteTabNavigation activeTab="hc" />
    </div>
  );
}
